import { AttachmentCategory } from '@policyfly/protobuf'
import {
  AttachmentDetail,
  AttachmentDetail_ContentLocation_Field,
  AttachmentDetail_Download_Format,
  AttachmentDetail_SignatureStatus,
  AttachmentDetail_Status,
  AttachmentDetail_VirusStatus,
  AttachmentVisibility as PatrickAttachmentVisibility,
} from '@policyfly/protobuf/patrick'

import { userJsonToUserDetail } from '@/utils/protobuf/mappers/user'

import type {
  AttachmentDetail_Download,
  AttachmentDetail_Preview,
} from '@policyfly/protobuf/patrick'
import type { SystemAttachmentCategory, UserDefinedAttachmentCategory } from '@policyfly/schema/types/shared/attachmentCategory'
import type { ApplicationAttachment } from 'types/application'

type LegacyAttachmentCategory = SystemAttachmentCategory | UserDefinedAttachmentCategory | ''

/**
 * Converts legacy Django {@link LegacyAttachmentCategory attachment category}
 * to equivalent Protobuf Alexandria {@link AttachmentCategory}.
 *
 * Coverage specific categories such as 'AQT' and 'MQT' have no equivalent and will
 * always map to Unspecified.
 */
export function stringToEnumAttachmentCategory (category: LegacyAttachmentCategory | undefined): AttachmentCategory {
  if (!category) return AttachmentCategory.Attachment_Category_Unspecified
  return stringToEnumAttachmentCategoryMap.get(category) ?? AttachmentCategory.Attachment_Category_Unspecified
}

const stringToEnumAttachmentCategoryMap = new Map<LegacyAttachmentCategory, AttachmentCategory>([
  ['XXX', AttachmentCategory.Attachment_Category_Not_Applicable],
  ['INQ', AttachmentCategory.Attachment_Category_Inquiry],
  ['APP', AttachmentCategory.Attachment_Category_Application],
  ['QUO', AttachmentCategory.Attachment_Category_Quote],
  ['END', AttachmentCategory.Attachment_Category_Endorsement],
  ['CER', AttachmentCategory.Attachment_Category_Certificate],
  ['INV', AttachmentCategory.Attachment_Category_Broker_Invoice],
  ['INI', AttachmentCategory.Attachment_Category_Insured_Invoice],
  ['DEC', AttachmentCategory.Attachment_Category_Declarations],
  ['CLA', AttachmentCategory.Attachment_Category_Claim],
  ['ADD', AttachmentCategory.Attachment_Category_Addendum],
  ['MED', AttachmentCategory.Attachment_Category_Medical_Certification],
  ['TMP', AttachmentCategory.Attachment_Category_Temporary_Document],
  ['QSM', AttachmentCategory.Attachment_Category_Quote_Summary],
  ['EQS', AttachmentCategory.Attachment_Category_Endorsement_Quote_Summary],
  ['BND', AttachmentCategory.Attachment_Category_Binder],
  ['OTB', AttachmentCategory.Attachment_Category_Order_To_Bind],
  ['COC', AttachmentCategory.Attachment_Category_Confirmation_of_Coverage],
  ['DRI', AttachmentCategory.Attachment_Category_Drivers_Outside_Criteria],
  ['LOR', AttachmentCategory.Attachment_Category_Loss],
  ['PJD', AttachmentCategory.Attachment_Category_Project_Details],
  ['GLI', AttachmentCategory.Attachment_Category_General_Liabilities],
  ['OLI', AttachmentCategory.Attachment_Category_Other_Liabilities],
  ['GLR', AttachmentCategory.Attachment_Category_GL_Loss],
  ['ALR', AttachmentCategory.Attachment_Category_Auto_Loss],
  ['LLR', AttachmentCategory.Attachment_Category_Liquor_Loss],
  ['COV', AttachmentCategory.Attachment_Category_COVID_Procedure],
  ['CAA', AttachmentCategory.Attachment_Category_Carrier_Audit],
  ['EXP', AttachmentCategory.Attachment_Category_Exposure_Information],
  ['SEC', AttachmentCategory.Attachment_Category_Security],
  ['EVA', AttachmentCategory.Attachment_Category_Evacuation_Plan],
  ['APL', AttachmentCategory.Attachment_Category_APD_Loss],
  ['MTL', AttachmentCategory.Attachment_Category_MTC_Loss],
  ['COI', AttachmentCategory.Attachment_Category_Certificate_of_Insurance],
  ['LCR', AttachmentCategory.Attachment_Category_Loss_Control_Recommendations],
])

/**
 * Converts a legacy Django {@link ApplicationAttachment} to the unified {@link AttachmentDetail}.
 *
 * Returns `null` for a hard archived attachment, which has no representation in the
 * unified shape and is expected to be filtered out by the caller.
 */
export function applicationAttachmentToAttachmentDetail (attachment: ApplicationAttachment): AttachmentDetail | null {
  if (attachment.archived) return null

  const category = attachment.category as LegacyAttachmentCategory

  return AttachmentDetail.create({
    id: String(attachment.id),
    luid: attachment.luid || undefined,
    applicationId: attachment.applications?.[0] === undefined ? undefined : String(attachment.applications[0]),
    policyDocument: attachment.policy_document,
    category: stringToEnumAttachmentCategory(category),
    legacyCategory: category || undefined,
    documentType: attachment.document_type,
    filename: attachment.original_name,
    // django does not report a mime type, consumers fall back to the filename
    mimeType: '',
    sizeBytes: attachment.size ? String(attachment.size) : undefined,
    created: attachment.created,
    systemGenerated: attachment.system_generated,
    archived: attachment.soft_archived
      ? { restorable: !!attachment.restorable_from_soft_archive }
      : undefined,
    visibility: attachment.restricted_visibility
      ? PatrickAttachmentVisibility.ATTACHMENT_VISIBILITY_RESTRICTED
      : PatrickAttachmentVisibility.ATTACHMENT_VISIBILITY_UNRESTRICTED,
    // django has no concept of an unmergeable attachment, so they all are
    mergeable: true,
    owner: attachment.owned_by ? userJsonToUserDetail(attachment.owned_by) : undefined,
    status: applicationAttachmentToDetailStatus(attachment),
    virusStatus: stringToDetailVirusStatus(attachment.virus_status),
    signatureStatus: stringToDetailSignatureStatus(attachment.signature_status, attachment.signature_confirmed),
    preview: applicationAttachmentToPreview(attachment),
    downloads: applicationAttachmentToDownloads(attachment),
  })
}

/**
 * Converts a list of legacy Django attachments to the unified shape,
 * dropping any that are hard archived.
 */
export function applicationAttachmentsToAttachmentDetails (attachments: ApplicationAttachment[] | undefined): AttachmentDetail[] {
  return (attachments ?? []).reduce<AttachmentDetail[]>((acc, attachment) => {
    const detail = applicationAttachmentToAttachmentDetail(attachment)
    if (detail) acc.push(detail)
    return acc
  }, [])
}

/**
 * Builds the {@link AttachmentDetail_Preview} for a legacy Django {@link ApplicationAttachment}.
 *
 * Django has no name for the preview, so it is always empty.
 */
function applicationAttachmentToPreview (attachment: ApplicationAttachment): AttachmentDetail_Preview | undefined {
  if (attachment.pdf_url) {
    return {
      status: AttachmentDetail_Status.STATUS_READY,
      name: '',
      location: { location: { oneofKind: 'url', url: attachment.pdf_url } },
    }
  }
  switch (attachment.pdf_status) {
    // a preview reported as ready without a url has yet to be served one
    case 'PASSED':
    case 'PENDING':
      return { status: AttachmentDetail_Status.STATUS_PROCESSING, name: '' }
    case 'FAILED':
      return { status: AttachmentDetail_Status.STATUS_FAILED, name: '' }
    default:
      return undefined
  }
}

/**
 * Builds the download options for a legacy Django {@link ApplicationAttachment}.
 *
 * The preview is not included, as consumers offer it alongside these.
 */
function applicationAttachmentToDownloads (attachment: ApplicationAttachment): AttachmentDetail_Download[] {
  const downloads: AttachmentDetail_Download[] = [{
    format: AttachmentDetail_Download_Format.FORMAT_ORIGINAL,
    location: attachment.original_url
      ? { location: { oneofKind: 'url', url: attachment.original_url } }
      : { location: { oneofKind: 'field', field: AttachmentDetail_ContentLocation_Field.FIELD_ORIGINAL } },
  }]

  const pdf = attachment.pdf_download_url
  if (pdf) {
    downloads.push({
      format: AttachmentDetail_Download_Format.FORMAT_PDF,
      location: { location: { oneofKind: 'url', url: pdf } },
    })
  }

  const xlsx = attachment.xlsx_download_url || attachment.xlsx_url
  if (xlsx) {
    downloads.push({
      format: AttachmentDetail_Download_Format.FORMAT_XLSX,
      location: { location: { oneofKind: 'url', url: xlsx } },
    })
  }

  return downloads
}

/**
 * Determines the availability of a legacy Django {@link ApplicationAttachment}'s own data.
 *
 * A conversion that is still running leaves the attachment processing even once its original is
 * available, as that is what consumers poll on to pick the converted formats up.
 * A failed conversion does not, as the original remains downloadable.
 */
function applicationAttachmentToDetailStatus (attachment: ApplicationAttachment): AttachmentDetail_Status {
  if (attachment.pdf_status === 'PENDING' || attachment.xlsx_status === 'PENDING') return AttachmentDetail_Status.STATUS_PROCESSING
  if (attachment.original_url) return AttachmentDetail_Status.STATUS_READY
  if (attachment.pdf_status === 'FAILED' || attachment.xlsx_status === 'FAILED') return AttachmentDetail_Status.STATUS_FAILED
  return AttachmentDetail_Status.STATUS_READY
}

/**
 * Converts a legacy Django virus status string
 * to an equivalent {@link AttachmentDetail_VirusStatus}.
 */
export function stringToDetailVirusStatus (status: string): AttachmentDetail_VirusStatus {
  return stringToDetailVirusStatusMap.get(status) ?? AttachmentDetail_VirusStatus.VIRUS_STATUS_UNSPECIFIED
}

const stringToDetailVirusStatusMap = new Map<string, AttachmentDetail_VirusStatus>([
  ['PENDING', AttachmentDetail_VirusStatus.VIRUS_STATUS_PENDING],
  ['PASSED', AttachmentDetail_VirusStatus.VIRUS_STATUS_PASSED],
  ['FAILED', AttachmentDetail_VirusStatus.VIRUS_STATUS_FAILED],
  ['UNAVAILABLE', AttachmentDetail_VirusStatus.VIRUS_STATUS_UNAVAILABLE],
])

/**
 * Converts a legacy Django signature status and its confirmation
 * to an equivalent {@link AttachmentDetail_SignatureStatus}.
 *
 * Django tracks whether a signature was accepted separately from the signature itself,
 * which the unified shape folds into the one status.
 */
export function stringToDetailSignatureStatus (
  status: string,
  confirmed: boolean | null,
): AttachmentDetail_SignatureStatus {
  switch (status) {
    case 'SIGNED':
      if (confirmed === true) return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_APPROVED
      if (confirmed === false) return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_REJECTED
      return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED
    case 'DECLINED': return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_REFUSED
    case 'CANCELLED': return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_CANCELLED
    case 'DESYNCED':
    case 'ERROR': return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_FAILED
    default: return AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_UNSPECIFIED
  }
}
